import { Injectable } from '@angular/core';
import { GlobalValuesService } from '../../../core/services/global-values.service';

@Injectable({
  providedIn: 'root',
})
export class OpportunityDetailsHelperService {
  constructor(private globalValuesService: GlobalValuesService) {}

  /**
   * Obtiene el nombre completo del contacto asociado a la oportunidad
   */
  getContactName(opportunity: any): string {
    if (!opportunity) return '-';

    // Primero intentar obtener del objeto contactPartner si existe
    if (opportunity.contactPartner) {
      const contact = opportunity.contactPartner;
      return (
        [contact.name, contact.surName].filter(Boolean).join(' ').trim() || '-'
      );
    }

    // Si no existe, buscar por partnerId en la lista de partners
    if (opportunity.partnerId) {
      const partner = this.globalValuesService.partnerlist?.find(
        (p: any) => p.id === opportunity.partnerId
      );
      if (partner) {
        return (
          [partner.name, partner.surName].filter(Boolean).join(' ').trim() ||
          partner.name ||
          '-'
        );
      }
    }
    return '-';
  }

  /**
   * Obtiene el nombre completo del comercial/usuario asignado
   */
  getCommercialName(opportunity: any): string {
    // Primero intentar obtener del objeto user si existe
    if (opportunity?.comercialUser) {
      const commercial = opportunity.comercialUser;
      return (
        [commercial.name, commercial.surName]
          .filter(Boolean)
          .join(' ')
          .trim() || '-'
      );
    }

    // Si no existe, buscar por userId en la lista de usuarios
    if (opportunity?.userId) {
      const users = this.globalValuesService.users$.value || [];
      const user = users.find((u: any) => u.id === opportunity.userId);
      if (user?.partner) {
        return (
          [user.partner.name, user.partner.surName]
            .filter(Boolean)
            .join(' ')
            .trim() ||
          user.partner.name ||
          '-'
        );
      }
    }
    return '-';
  }

  /**
   * Obtiene el nombre de una etapa por su ID
   */
  getStageName(stageId: number | null, stages: any[]): string {
    if (!stageId || !stages || stages.length === 0) return '-';
    const stage = stages.find((s: any) => s.id === stageId);
    return stage?.name || '-';
  }

  /**
   * Obtiene el objeto de etapa actual
   */
  getCurrentStage(opportunity: any, stages: any[]): any {
    if (!opportunity?.stageId || !stages) return null;
    return stages.find((s: any) => s.id === opportunity.stageId);
  }

  /**
   * Obtiene el ícono Material correspondiente a una etapa por su índice
   */
  getStageIcon(index: number): string {
    const icons = [
      'fiber_new', // Nuevo
      'assessment', // Evaluación
      'request_quote', // Presupuesto
      'pause_circle', // Pausada
      'check_circle', // Adjudicada
      'cancel', // Cancelada
    ];
    return icons[index] || 'circle';
  }

  /**
   * Obtiene el color hexadecimal correspondiente a una etapa por su índice
   */
  getStageColor(index: number): string {
    const colors = [
      '#0072F5', // Azul - Nuevo
      '#8E4EC6', // Púrpura - Evaluación
      '#F59E0B', // Amarillo - Presupuesto
      '#64748B', // Gris - Pausada
      '#6CDA75', // Verde - Adjudicada
      '#CB2A2F', // Rojo - Cancelada
    ];
    return colors[index] || '#64748B';
  }

  /**
   * Obtiene la clase CSS para el efecto hover de una etapa
   */
  getStageHoverClass(index: number): string {
    const classes = [
      'blue-hover',
      'purple-hover',
      'yellow-hover',
      'gray-hover',
      'green-hover',
      'red-hover',
    ];
    return classes[index] || 'gray-hover';
  }

  /**
   * Compara dos valores para determinar si han cambiado
   */
  hasValueChanged(current: any, backup: any): boolean {
    // Caso trivial: son exactamente iguales
    if (current === backup) return false;

    // Manejo de null/undefined
    if (current === null || current === undefined) {
      return backup !== null && backup !== undefined;
    }
    if (backup === null || backup === undefined) {
      return current !== null && current !== undefined;
    }

    // Si ambos son objetos, comparar como JSON
    if (typeof current === 'object' && typeof backup === 'object') {
      return JSON.stringify(current) !== JSON.stringify(backup);
    }

    // Comparar como strings para otros tipos
    return String(current) !== String(backup);
  }

  /**
   * Obtiene el nombre de la oportunidad desde diferentes campos posibles
   */
  getOpportunityName(
    opportunity: any,
    originalOpportunityName: string
  ): string {
    if (!opportunity) return '-';

    // Para leads el nombre SIEMPRE es el campo raíz "name" del row (ej. "Lead CEC 6"), NUNCA partner.name
    const isLead = opportunity.type === 'Lead';
    if (isLead) {
      const rootName =
        opportunity.name ||
        opportunity.oportunidad ||
        opportunity.originalOpportunityName;
      if (rootName && !String(rootName).includes('@')) {
        const contactName =
          opportunity.contactPartner || opportunity.partner
            ? [
                (opportunity.contactPartner || opportunity.partner).name,
                (opportunity.contactPartner || opportunity.partner).surName,
              ]
                .filter(Boolean)
                .join(' ')
                .trim()
            : '';
        if (!contactName || String(rootName).trim() !== contactName) {
          return String(rootName).trim();
        }
      }
      if (originalOpportunityName && !originalOpportunityName.includes('@')) {
        return originalOpportunityName;
      }
      return '-';
    }

    // Oportunidades: prioridad nombre guardado en drawer, luego name, oportunidad, etc.
    if (
      originalOpportunityName &&
      !originalOpportunityName.includes('@')
    ) {
      return originalOpportunityName;
    }
    if (opportunity.name && !opportunity.name.includes('@')) {
      return opportunity.name;
    }
    if (opportunity.oportunidad && !opportunity.oportunidad.includes('@')) {
      return opportunity.oportunidad;
    }
    if (opportunity.originalOpportunityName) {
      return opportunity.originalOpportunityName;
    }
    return '-';
  }
}
